import type { DocumentTypeVariable, DocumentTypeVariableCategory } from '@/document-types/domain/types/DocumentType';

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord => typeof value === 'object' && value !== null && !Array.isArray(value);

const readString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

const allowedVariableTypes = new Set(['string', 'number', 'date', 'boolean']);

const normalizeVariableType = (value: unknown): DocumentTypeVariable['type'] => {
  if (typeof value !== 'string') return 'string';
  const lower = value.toLowerCase();
  if (allowedVariableTypes.has(lower)) return lower as DocumentTypeVariable['type'];
  if (lower === 'text' || lower === 'email') return 'string';
  if (lower === 'currency' || lower === 'decimal' || lower === 'integer') return 'number';
  return 'string';
};

const mapVariable = (dto: unknown): DocumentTypeVariable | null => {
  if (!isRecord(dto)) return null;

  const path = readString(dto.path);
  const label = readString(dto.label);
  if (!path || !label) return null;

  const type = normalizeVariableType(dto.type);
  const example = readString(dto.example);
  const description = readString(dto.description);

  return { path, label, type, ...(example ? { example } : {}), ...(description ? { description } : {}) };
};

const mapCategory = (dto: unknown): DocumentTypeVariableCategory | null => {
  if (!isRecord(dto)) return null;

  const key = readString(dto.key);
  const label = readString(dto.label);
  const variablesRaw = Array.isArray(dto.variables) ? dto.variables : [];

  const variables = variablesRaw.map(mapVariable).filter((v): v is DocumentTypeVariable => v !== null);
  if (!key || !label || variables.length === 0) return null;

  return { key, label, variables };
};

export const mapVariablesCatalogDto = (dto: unknown): DocumentTypeVariableCategory[] => {
  if (!isRecord(dto)) return [];

  const categoriesRaw = dto.variableCategories;
  if (!Array.isArray(categoriesRaw)) return [];

  return categoriesRaw.map(mapCategory).filter((c): c is DocumentTypeVariableCategory => c !== null);
};
